import classNames from 'classnames';
import { useRef, useState } from 'react';
import type { EssencePool } from '@sorrem/shared';
import { Popover } from '@sorrem/ui';
import { Button, EssenceTally } from '@sorrem-client/ui/components';
import styles from './TradeBar.module.scss';

interface TradeBarProps {
    /** The item a trade gives up, named in the confirmation. */
    itemName: string;
    /** What a house of exchange pays for it. */
    essence: EssencePool;
    /** While a trade is under way, the button waits. */
    isTrading: boolean;
    onTrade: () => void;
    className?: string;
}

/**
 * One line for trading the selected item to a house of exchange: what it is
 * worth, as essence orbs, and a small Trade button. The trade is only made
 * once confirmed in a popover over the button, which names the item and
 * the essence it pays.
 */
export function TradeBar({ itemName, essence, isTrading, onTrade, className }: TradeBarProps): JSX.Element {
    const [isConfirming, setIsConfirming] = useState(false);
    // The popover anchors to an element; the shared Button doesn't forward a ref.
    const anchorRef = useRef<HTMLSpanElement>(null);

    return (
        <div className={classNames(styles.tradeBar, className)}>
            <EssenceTally essencePool={essence} ariaLabel='Essence the trade pays' label='Worth' isEmptyHidden />
            <span ref={anchorRef}>
                <Button variant='secondary' size='small' disabled={isTrading} aria-expanded={isConfirming} onClick={() => setIsConfirming((open) => !open)}>
                    Trade
                </Button>
            </span>
            <Popover open={isConfirming} anchorRef={anchorRef} placement='top' label={`Trade ${itemName}`} onClose={() => setIsConfirming(false)}>
                <div className={styles.confirm}>
                    <p className={styles.ask}>{`Trade ${itemName}?`}</p>
                    <EssenceTally essencePool={essence} ariaLabel='Essence the trade pays' label='A house of exchange pays' isEmptyHidden />
                    <div className={styles.actions}>
                        <Button variant='secondary' size='small' onClick={() => setIsConfirming(false)}>Keep</Button>
                        <Button
                            size='small'
                            onClick={() => {
                                setIsConfirming(false);
                                onTrade();
                            }}
                        >
                            Trade
                        </Button>
                    </div>
                </div>
            </Popover>
        </div>
    );
}
